import { 
  addDays, 
  addWeeks, 
  addMonths, 
  addQuarters, 
  addYears, 
  format, 
  isAfter, 
  isBefore,
  startOfDay,
  endOfDay,
  eachDayOfInterval,
  differenceInDays 
} from 'date-fns';
import { dbService } from './dbService.js';
import { allocateDailyAmounts, countCoverageDays, calendarDate } from './financialMath.js';

class RecurringService {
  /**
   * Generate transactions from recurring rules
   */
  async generateTransactionsFromRules(ruleIds, occurrenceIds = null, options = {}) {
    const {
      generateFuture = true, generatePast = false, maxOccurrences = 12,
      endDate = null, generateDailyTransactions = true, onProgress = () => {}
    } = options;

    try {
      onProgress({ stage: 'preparing', percent: 5, message: 'Checking recurring rules…' });
      // Give React time to display the blocking overlay before any expensive work.
      await new Promise(resolve => setTimeout(resolve, 0));
      const allRules = await dbService.getRecurringRules();
      const rules = ruleIds ? allRules.filter(rule => ruleIds.includes(rule.id)) : allRules;
      if (!rules.length) throw new Error('No recurring rules found');

      const now = new Date();
      const plans = rules.map(rule => {
        const occurrences = this.generateOccurrences(rule, {
          generateFuture, generatePast, maxOccurrences, endDate, currentDate: now
        });
        return {
          rule, occurrences: occurrenceIds
            ? occurrences.filter(occ => occurrenceIds.includes(occ.id))
            : occurrences
        };
      });
      const totalOccurrences = plans.reduce((n, plan) => n + plan.occurrences.length, 0);
      const pending = [];
      let prepared = 0;
      onProgress({
        stage: 'planning', percent: 15,
        message: totalOccurrences ? 'Preparing scheduled occurrences…' : 'Checking for scheduled occurrences…',
        completed: 0, total: totalOccurrences
      });

      for (const { rule, occurrences } of plans) {
        for (const occurrence of occurrences) {
          if (generateDailyTransactions && rule.frequency !== 'daily') {
            // Accrual and pending payment are different accounting entries;
            // amounts and cash confirmation semantics remain unchanged.
            pending.push({
              ...this.createTransactionFromOccurrence(rule, occurrence),
              status: 'pending', cashConfirmed: false, isDailyBreakdown: false
            });
            pending.push(...this.createDailyTransactionsFromOccurrence(rule, occurrence));
          } else {
            pending.push({
              ...this.createTransactionFromOccurrence(rule, occurrence),
              status: 'pending', cashConfirmed: false
            });
          }
          if (pending.length > 5000) {
            throw new Error('This would create more than 5,000 entries. Select fewer occurrences.');
          }
          prepared++;
          onProgress({
            stage: 'planning',
            percent: Math.min(70, 15 + Math.round(55 * prepared / Math.max(1, totalOccurrences))),
            message: 'Preparing scheduled entries…',
            completed: prepared, total: totalOccurrences
          });
        }
        // Allow the planning progress to paint between rules, before the write.
        await new Promise(resolve => setTimeout(resolve, 0));
      }

      onProgress({
        stage: 'saving', percent: 75, indeterminate: true,
        message: 'Checking duplicates and saving all entries safely…',
        planned: pending.length
      });
      await new Promise(resolve => setTimeout(resolve, 0));
      // One atomic transaction: failure cannot leave half a daily breakdown.
      const { saved, skipped } = await dbService.addRecurringTransactionsBatchIfAbsent(pending);

      onProgress({
        stage: 'refreshing', percent: 95,
        message: 'Verifying saved entries and updating the workspace…',
        saved: saved.length, skipped
      });
      for (const { rule } of plans) {
        await this.updateRuleLastGenerated(rule.id, now);
      }
      onProgress({
        stage: 'complete', percent: 100,
        message: 'Generation completed safely.', saved: saved.length, skipped
      });
      return {
        success: true, generatedCount: saved.length, transactions: saved,
        skippedDuplicates: skipped, rulesProcessed: rules.length
      };
    } catch (error) {
      console.error('Failed to generate transactions from recurring rules:', error);
      throw new Error('Failed to generate transactions: ' + error.message);
    }
  }

  /**
   * Create daily transactions from a single occurrence
   */
  createDailyTransactionsFromOccurrence(rule, occurrence) {
    const dailyTransactions = [];
    const coveragePeriod = occurrence.period;
    
    // Get all days in the coverage period
    const daysInPeriod = eachDayOfInterval({
      start: coveragePeriod.startDate,
      end: coveragePeriod.endDate
    });

    const dailyAmounts = allocateDailyAmounts(occurrence.amount, daysInPeriod.length);
    daysInPeriod.forEach((day, index) => {
      const dayNumber = index + 1;
      const isFirstDay = index === 0;
      const isLastDay = index === daysInPeriod.length - 1;
      
      const dailyTransaction = {
        type: rule.type,
        amount: dailyAmounts[index],
        category: rule.category,
        payee: rule.payee || `${rule.name} (Daily)`,
        date: format(day, 'yyyy-MM-dd'),
        notes: this.generateDailyTransactionNotes(rule, occurrence, day, dayNumber, daysInPeriod.length),
        tags: this.generateDailyTransactionTags(rule, occurrence, dayNumber, isFirstDay, isLastDay),
        person: rule.person || null,
        account: rule.account || null,
        // A scheduled allocation is not proof of payment.
        status: 'pending',
        recurringRuleId: rule.id,
        recurringOccurrenceId: occurrence.id,
        dailySequence: {
          dayNumber,
          totalDays: daysInPeriod.length,
          isFirstDay,
          isLastDay,
          periodStart: format(coveragePeriod.startDate, 'yyyy-MM-dd'),
          periodEnd: format(coveragePeriod.endDate, 'yyyy-MM-dd'),
        },
        coveragePeriod: {
          start: format(coveragePeriod.startDate, 'yyyy-MM-dd'),
          end: format(coveragePeriod.endDate, 'yyyy-MM-dd'),
          days: coveragePeriod.days,
          dailyRate: occurrence.dailyRate,
          totalAmount: occurrence.amount,
        },
        isRecurring: true,
        isDailyBreakdown: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      dailyTransactions.push(dailyTransaction);
    });

    return dailyTransactions;
  }

  /**
   * Generate notes for daily transactions
   */
  generateDailyTransactionNotes(rule, occurrence, day, dayNumber, totalDays) {
    const baseNotes = rule.notes || '';
    const dayInfo = `Day ${dayNumber} of ${totalDays}`;
    const periodInfo = `${rule.frequency} period: ${format(occurrence.date, 'MMM d')} - ${format(occurrence.endDate, 'MMM d, yyyy')}`;
    const dailyInfo = `Daily breakdown of ${rule.name}`;
    const specificDayInfo = `Transaction for ${format(day, 'EEEE, MMM d, yyyy')}`;
    
    return [baseNotes, dailyInfo, dayInfo, specificDayInfo, periodInfo].filter(Boolean).join(' | ');
  }

  /**
   * Generate tags for daily transactions
   */
  generateDailyTransactionTags(rule, occurrence, dayNumber, isFirstDay, isLastDay) {
    const tags = ['recurring', 'daily-breakdown', rule.frequency];
    
    if (rule.tags) {
      tags.push(...(Array.isArray(rule.tags) ? rule.tags : [rule.tags]));
    }
    
    if (occurrence.status === 'future') {
      tags.push('future');
    }
    
    if (isFirstDay) {
      tags.push('period-start');
    }
    
    if (isLastDay) {
      tags.push('period-end');
    }
    
    tags.push(`day-${dayNumber}`);
    
    return tags;
  }

  /**
   * Generate occurrences for a recurring rule
   */
  generateOccurrences(rule, options = {}) {
    const { generateFuture = true, generatePast = false, maxOccurrences = 12,
      endDate = null, currentDate = new Date() } = options;
    if (!Number.isInteger(maxOccurrences) || maxOccurrences < 1) return [];
    const anchor = calendarDate(rule.startDate);
    if (!anchor) throw new Error('Invalid recurring start date');
    const startDate = new Date(anchor + 'T12:00:00');
    const today = calendarDate(currentDate);
    const limits = [rule.endDate, endDate].filter(Boolean).map(calendarDate);
    if (limits.includes('')) throw new Error('Invalid recurring end date');
    const limit = limits.length ? limits.sort()[0] : null;
    const historical = [];
    let index = 0;

    const entry = (n) => {
      const date = this.getAnchoredOccurrenceDate(startDate, rule.frequency, n);
      const next = this.getAnchoredOccurrenceDate(startDate, rule.frequency, n + 1);
      const period = this.calculateCoveragePeriod(date, rule.frequency, next);
      return { id: 'occ-' + calendarDate(date), date, endDate: period.endDate,
        amount: rule.amount, dailyRate: rule.amount / period.days,
        status: calendarDate(date) <= today ? 'past' : 'future', period };
    };

    // Walk anchored occurrences. Taking the last N past occurrences avoids
    // generating years-old periods before the most relevant recent ones.
    while (index < 100000) {
      const date = this.getAnchoredOccurrenceDate(startDate, rule.frequency, index);
      if (calendarDate(date) > today) break;
      if (limit && calendarDate(date) > limit) break;
      historical.push(entry(index));
      if (historical.length > maxOccurrences + 1) historical.shift();
      index++;
    }
    if (index === 100000) throw new Error('Recurring schedule exceeds safe length');

    const results = [];
    if (generatePast) results.push(...historical.slice(-maxOccurrences));
    if (generateFuture && !generatePast && historical.length) {
      const active = historical[historical.length - 1];
      if (calendarDate(active.endDate) >= today) results.push(active);
    }
    if (generateFuture) {
      let futureCount = 0;
      while (futureCount < maxOccurrences) {
        const occurrence = entry(index++);
        if (limit && calendarDate(occurrence.date) > limit) break;
        results.push(occurrence);
        futureCount++;
      }
    }
    return results;
  }

  // Recurrence always advances from the rule's original anchor, so a Jan 31
  // monthly schedule produces Feb 28 then Mar 31, not Mar 28.
  getAnchoredOccurrenceDate(anchor, frequency, index) {
    switch (frequency.toLowerCase()) {
      case 'daily': return addDays(anchor,index);
      case 'weekly': return addWeeks(anchor,index);
      case 'fortnightly': case 'biweekly': return addWeeks(anchor,2*index);
      case 'monthly': return addMonths(anchor,index);
      case 'quarterly': return addQuarters(anchor,index);
      case 'yearly': case 'annually': return addYears(anchor,index);
      default: throw new Error('Unsupported recurrence frequency: ' + frequency);
    }
  }

  /**
   * Get the next occurrence date based on frequency
   */
  getNextOccurrenceDate(currentDate, frequency) {
    switch (frequency.toLowerCase()) {
      case 'daily':
        return addDays(currentDate, 1);
      case 'weekly':
        return addWeeks(currentDate, 1);
      case 'fortnightly':
      case 'biweekly':
        return addWeeks(currentDate, 2);
      case 'monthly':
        return addMonths(currentDate, 1);
      case 'quarterly':
        return addQuarters(currentDate, 1);
      case 'yearly':
      case 'annually':
        return addYears(currentDate, 1);
      default:
        return addMonths(currentDate, 1); // Default to monthly
    }
  }

  getPreviousOccurrenceDate(currentDate, frequency) {
    switch (frequency.toLowerCase()) {
      case 'daily': return addDays(currentDate, -1);
      case 'weekly': return addWeeks(currentDate, -1);
      case 'fortnightly': case 'biweekly': return addWeeks(currentDate, -2);
      case 'monthly': return addMonths(currentDate, -1);
      case 'quarterly': return addQuarters(currentDate, -1);
      case 'yearly': case 'annually': return addYears(currentDate, -1);
      default: return addMonths(currentDate, -1);
    }
  }

  /**
   * Calculate the coverage period for an occurrence
   */
  calculateCoveragePeriod(startDate, frequency, nextStart = null) {
    if (nextStart) {
      const endDate = endOfDay(addDays(nextStart, -1));
      return { startDate: startOfDay(startDate), endDate,
        days: countCoverageDays(startDate, endDate) };
    }
    let endDate;
    let days;

    switch (frequency.toLowerCase()) {
      case 'daily':
        endDate = endOfDay(startDate);
        days = 1;
        break;
      case 'weekly':
        endDate = endOfDay(addDays(startDate, 6));
        days = 7;
        break;
      case 'fortnightly':
      case 'biweekly':
        endDate = endOfDay(addDays(startDate, 13));
        days = 14;
        break;
      case 'monthly':
        endDate = endOfDay(addDays(addMonths(startDate, 1), -1));
        days = countCoverageDays(startDate, endDate);
        break;
      case 'quarterly':
        endDate = endOfDay(addDays(addQuarters(startDate, 1), -1));
        days = countCoverageDays(startDate, endDate);
        break;
      case 'yearly':
      case 'annually':
        endDate = endOfDay(addDays(addYears(startDate, 1), -1));
        days = countCoverageDays(startDate, endDate);
        break;
      default:
        // Default to monthly
        endDate = endOfDay(addDays(addMonths(startDate, 1), -1));
        days = countCoverageDays(startDate, endDate);
    }

    return {
      startDate: startOfDay(startDate),
      endDate,
      days,
    };
  }

  /**
   * Create a transaction from a rule occurrence (original single transaction method)
   */
  createTransactionFromOccurrence(rule, occurrence) {
    return {
      type: rule.type,
      amount: occurrence.amount,
      category: rule.category,
      payee: rule.payee || `${rule.name} (Recurring)`,
      date: format(occurrence.date, 'yyyy-MM-dd'),
      notes: this.generateTransactionNotes(rule, occurrence),
      tags: this.generateTransactionTags(rule, occurrence),
      person: rule.person || null,
      account: rule.account || null,
      status: 'pending',
      recurringRuleId: rule.id,
      recurringOccurrenceId: occurrence.id,
      coveragePeriod: {
        start: format(occurrence.date, 'yyyy-MM-dd'),
        end: format(occurrence.endDate, 'yyyy-MM-dd'),
        days: occurrence.period.days,
        dailyRate: occurrence.dailyRate,
        totalAmount: occurrence.amount,
      },
      isRecurring: true,
      isDailyBreakdown: false,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
  }

  /**
   * Generate notes for the transaction (original method)
   */
  generateTransactionNotes(rule, occurrence) {
    const baseNotes = rule.notes || '';
    const periodInfo = `Coverage: ${format(occurrence.date, 'MMM d')} - ${format(occurrence.endDate, 'MMM d, yyyy')}`;
    const dailyInfo = `Daily rate: $${occurrence.dailyRate.toFixed(2)}`;
    
    return [baseNotes, periodInfo, dailyInfo].filter(Boolean).join(' | ');
  }

  /**
   * Generate tags for the transaction (original method)
   */
  generateTransactionTags(rule, occurrence) {
    const tags = ['recurring', rule.frequency];
    
    if (rule.tags) {
      tags.push(...(Array.isArray(rule.tags) ? rule.tags : [rule.tags]));
    }
    
    if (occurrence.status === 'future') {
      tags.push('future');
    }
    
    return tags;
  }

  /**
   * Update rule's last generated date
   */
  async updateRuleLastGenerated(ruleId, date) {
    try {
      await dbService.updateRecurringRule(ruleId, {
        lastGeneratedAt: date,
        lastGeneratedCount: await this.getGeneratedTransactionCount(ruleId),
      });
    } catch (error) {
      console.error('Failed to update rule last generated date:', error);
      // Non-critical error, don't throw
    }
  }

  /**
   * Get count of transactions generated from a rule
   */
  async getGeneratedTransactionCount(ruleId) {
    try {
      const transactions = await dbService.getTransactions();
      return transactions.filter(t => t.recurringRuleId === ruleId).length;
    } catch (error) {
      console.error('Failed to get generated transaction count:', error);
      return 0;
    }
  }

  /**
   * Get transactions generated from recurring rules
   */
  async getRecurringTransactions(ruleId = null) {
    try {
      const transactions = await dbService.getTransactions();
      const recurringTransactions = transactions.filter(t => t.isRecurring);
      
      if (ruleId) {
        return recurringTransactions.filter(t => t.recurringRuleId === ruleId);
      }
      
      return recurringTransactions;
    } catch (error) {
      console.error('Failed to get recurring transactions:', error);
      return [];
    }
  }

  /**
   * Get daily breakdown transactions for a specific occurrence
   */
  async getDailyBreakdownTransactions(ruleId, occurrenceId) {
    try {
      const transactions = await dbService.getTransactions();
      return transactions.filter(t => 
        t.recurringRuleId === ruleId && 
        t.recurringOccurrenceId === occurrenceId &&
        t.isDailyBreakdown === true
      );
    } catch (error) {
      console.error('Failed to get daily breakdown transactions:', error);
      return [];
    }
  }

  /**
   * Delete transactions generated from a recurring rule
   */
  async deleteRecurringTransactions(ruleId, occurrenceIds = null) {
    try {
      const recurringTransactions = await this.getRecurringTransactions(ruleId);
      
      let transactionsToDelete = recurringTransactions;
      if (occurrenceIds) {
        transactionsToDelete = recurringTransactions.filter(t => 
          occurrenceIds.includes(t.recurringOccurrenceId)
        );
      }

      const deletePromises = transactionsToDelete.map(t => dbService.deleteTransaction(t.id));
      await Promise.all(deletePromises);

      return {
        success: true,
        deletedCount: transactionsToDelete.length,
        deletedTransactions: transactionsToDelete,
      };
    } catch (error) {
      console.error('Failed to delete recurring transactions:', error);
      throw new Error(`Failed to delete recurring transactions: ${error.message}`);
    }
  }

  /**
   * Preview transactions that would be generated from rules
   */
  previewTransactionsFromRules(rules, options = {}) {
    const { generateDailyTransactions = true } = options;
    const previews = [];
    
    for (const rule of rules) {
      const occurrences = this.generateOccurrences(rule, options);
      
      const rulePreview = {
        rule,
        occurrences: occurrences.map(occurrence => {
          let transactions = [];
          
          if (generateDailyTransactions && rule.frequency !== 'daily') {
            // Preview daily transactions
            transactions = this.createDailyTransactionsFromOccurrence(rule, occurrence);
          } else {
            // Preview single transaction
            transactions = [this.createTransactionFromOccurrence(rule, occurrence)];
          }

          return {
            ...occurrence,
            transactions,
            transactionCount: transactions.length,
          };
        }),
        totalAmount: occurrences.reduce((sum, occ) => sum + occ.amount, 0),
        count: occurrences.length,
        totalTransactions: occurrences.reduce((sum, occ) => {
          if (generateDailyTransactions && rule.frequency !== 'daily') {
            return sum + occ.period.days;
          }
          return sum + 1;
        }, 0),
      };
      
      previews.push(rulePreview);
    }
    
    return {
      previews,
      totalOccurrences: previews.reduce((sum, p) => sum + p.count, 0),
      totalTransactions: previews.reduce((sum, p) => sum + p.totalTransactions, 0),
      totalAmount: previews.reduce((sum, p) => sum + p.totalAmount, 0),
    };
  }

  /**
   * Auto-generate transactions for all active rules with daily breakdown
   */
  async autoGenerateTransactions(options = {}) {
    const {
      lookAheadDays = 30,
      maxPerRule = 3,
      generateDailyTransactions = true,
      onProgress = () => {},
    } = options;

    try {
      const rules = await dbService.getRecurringRules();
      const activeRules = rules.filter(rule => {
        // Skip if rule has an end date in the past
        if (rule.endDate && isBefore(new Date(rule.endDate), new Date())) {
          return false;
        }
        return true;
      });

      if (activeRules.length === 0) {
        return {
          success: true,
          message: 'No active recurring rules found',
          generatedCount: 0,
        };
      }

      const endDate = addDays(new Date(), lookAheadDays);
      
      const result = await this.generateTransactionsFromRules(
        activeRules.map(r => r.id),
        null,
        {
          generateFuture: true,
          generatePast: false,
          maxOccurrences: maxPerRule,
          endDate,
          generateDailyTransactions,
          onProgress,
        }
      );

      return {
        ...result,
        message: `Auto-generated ${result.generatedCount} transactions from ${result.rulesProcessed} rules${generateDailyTransactions ? ' (with daily breakdown)' : ''}`,
      };

    } catch (error) {
      console.error('Auto-generation failed:', error);
      throw error;
    }
  }

  /**
   * Get rule statistics with daily breakdown info
   */
  async getRuleStatistics(ruleId) {
    try {
      const rule = (await dbService.getRecurringRules()).find(r => r.id === ruleId);
      if (!rule) {
        throw new Error('Rule not found');
      }

      const transactions = await this.getRecurringTransactions(ruleId);
      const dailyTransactions = transactions.filter(t => t.isDailyBreakdown);
      const periodTransactions = transactions.filter(t => !t.isDailyBreakdown);
      
      const totalGenerated = transactions.length;
      const totalAmount = transactions.reduce((sum, t) => sum + t.amount, 0);
      const postedTransactions = transactions.filter(t => t.status === 'posted');
      const pendingTransactions = transactions.filter(t => t.status === 'pending');

      return {
        rule,
        totalGenerated,
        totalAmount,
        postedCount: postedTransactions.length,
        pendingCount: pendingTransactions.length,
        dailyTransactionCount: dailyTransactions.length,
        periodTransactionCount: periodTransactions.length,
        averageAmount: totalGenerated > 0 ? totalAmount / totalGenerated : 0,
        lastGenerated: rule.lastGeneratedAt,
        nextOccurrence: this.getNextOccurrenceDate(new Date(), rule.frequency),
      };
    } catch (error) {
      console.error('Failed to get rule statistics:', error);
      throw error;
    }
  }

  /**
   * Generate transactions with option to choose between daily breakdown or period totals
   */
  async generateTransactionsWithOptions(ruleIds, occurrenceIds, options = {}) {
    const {
      generateDailyTransactions = true,
      ...otherOptions
    } = options;

    return await this.generateTransactionsFromRules(ruleIds, occurrenceIds, {
      ...otherOptions,
      generateDailyTransactions,
    });
  }
}

export const recurringService = new RecurringService();